/**
 * Relationship Validation Schemas
 *
 * Centralized Zod schemas for relationship-related validation.
 * Used by RelationshipChooserModal and relationship creation flows.
 *
 * Note: This module focuses on INPUT validation (form-level).
 * Graph-based validation (cycles, duplicates, etc.) remains in
 * features/dashboard/validation/ as it requires full tree context.
 *
 * @module lib/validation/relationship.schema
 */

import { z } from 'zod';
import i18next from 'i18next';

/**
 * Relationship type enum schema
 * Defines the types of relationships that can be created
 */
export const relationshipTypeSchema = z.enum(
  ['father', 'mother', 'brother', 'sister', 'son', 'daughter', 'spouse'],
  {
    errorMap: () => ({ message: () => i18next.t('validation.invalidRelationshipType', { ns: 'common' }) }),
  }
);

/**
 * Relationship direction enum schema
 * Used for parent-child relationship directionality
 */
export const relationshipDirectionSchema = z.enum(['parent_child', 'spouse'], {
  errorMap: () => ({ message: () => i18next.t('validation.invalidRelationshipDirection', { ns: 'common' }) }),
});

/**
 * Partner selection schema (for child creation with other parent)
 */
export const partnerSelectionSchema = z.object({
  type: z.enum(['existing', 'new']),
  partnerId: z.string().uuid().optional(),
});

/**
 * Relationship creation input schema
 * Basic validation for relationship creation forms
 */
export const relationshipCreationSchema = z.object({
  relationshipType: relationshipTypeSchema,
  sourcePersonId: z.string().uuid({
    message: () => i18next.t('validation.invalidPersonId', { ns: 'common' }),
  }),
  targetPersonId: z.string().uuid({
    message: () => i18next.t('validation.invalidPersonId', { ns: 'common' }),
  }),
  partnerSelection: partnerSelectionSchema.optional(),
});

/**
 * Validate that source and target person IDs are different
 * (prevents self-relationships at the input level)
 */
export const relationshipCreationWithSelfCheckSchema = relationshipCreationSchema.refine(
  (data) => data.sourcePersonId !== data.targetPersonId,
  {
    message: () => i18next.t('validation.cannotRelateToSelf', { ns: 'common' }),
    path: ['targetPersonId'],
  }
);

// ==================== TYPE EXPORTS ====================

/**
 * Inferred TypeScript type for relationship type
 */
export type RelationshipType = z.infer<typeof relationshipTypeSchema>;

/**
 * Inferred TypeScript type for relationship direction
 */
export type RelationshipDirection = z.infer<typeof relationshipDirectionSchema>;

/**
 * Inferred TypeScript type for partner selection
 */
export type PartnerSelection = z.infer<typeof partnerSelectionSchema>;

/**
 * Inferred TypeScript type for relationship creation input
 */
export type RelationshipCreationInput = z.infer<typeof relationshipCreationSchema>;
